import { useAPI, useAuth, useToastModule } from '@/modules';
import {
  CampaignData, AppPagePerspective, Perspective, CampaignStatus, User,
} from '@/data/types';
import { ref, watch } from 'vue';
import { useCampaignsList } from './campaigns-list-module';

const { user } = useAuth();

export const useCampaignContracts = (perspective: AppPagePerspective) => {
  const toast = useToastModule();

  /// CAMPAIGN SUBPAGE
  const selectedCampaignID = ref<string>('');
  const contracts = ref<Array<CampaignData>>([]);
  const associatedCampaigns = ref<Array<CampaignData>>([]);

  const { loading, finished, data: loadedContractCampaigns, execute } = useAPI<Array<CampaignData>>('/campaigns/contracts/', false);
  const allCampaigns = useCampaignsList(perspective);

  const loadContracts = () => {
    const creatorID = user?.value?.id;
    const brandID = user?.value?.brand?.id;
    const targetID = perspective === Perspective.BRAND ? brandID : creatorID;

    if (perspective === Perspective.BRAND && !brandID) {
      throw new Error('Could not load campaigns for a brand, because brandID is missing!');
    }

    if (perspective === Perspective.BRAND || perspective === Perspective.ADMIN) {
      allCampaigns.loadCampaigns();
    }

    return execute({ params: { perspective, targetID } });
  };

  watch(loadedContractCampaigns, () => {
    if (perspective === Perspective.CREATOR) {
      associatedCampaigns.value = loadedContractCampaigns.value || [];
    }
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    filterList();
  });

  watch(allCampaigns.campaigns, () => {
    associatedCampaigns.value = allCampaigns
      .campaigns.value?.filter((campaign) => campaign.status !== CampaignStatus.Draft)
      || [];
  });

  // filtering

  const filterList = () => {
    const campaignsWithContracts = loadedContractCampaigns
      .value?.filter((campaign) => !!campaign.contracts?.length) || [];

    if (selectedCampaignID.value) {
      contracts.value = campaignsWithContracts.filter((campaign) => campaign.id === selectedCampaignID.value);
    } else {
      contracts.value = campaignsWithContracts;
    }
  };

  const filterByCampaignID = (campaignID = '') => {
    selectedCampaignID.value = campaignID;
    filterList();
  };

  /// SINGLE CONTRACT
  const {
    loading: loadingSingle,
    finished: finishedSingle,
    error: singleContractCampaignError,
    data: singleContractCampaign,
    execute: sendSingleContractRequest,
  } = useAPI<{ campaign: CampaignData; agencyOwner?: User }>('', false);
  const loadSingle = (campaignID: string, contractID: string) => sendSingleContractRequest({
    url: `/campaigns/contracts/${campaignID}/${contractID}`,
    params: { perspective },
  });

  // complete contract
  const {
    loading: completing,
    error: completingError,
    data: completingResult,
    execute: sendCompleteRequest,
  } = useAPI<CampaignData>('', false);

  const complete = (campaignID: string, contractID: string) => sendCompleteRequest({
    url: `/campaigns/contracts/${campaignID}/${contractID}`,
    method: 'PUT',
    data: { action: 'complete' },
  });
  watch(completingResult, toast.handleRequestSuccess('Contract completed successfully!'));
  watch(completingError, toast.handleRequestError);
  // < complete contract

  return {
    contracts,
    loading,
    finished,
    loadContracts,
    filterByCampaignID,
    associatedCampaigns,

    // single
    loadSingle,
    loadingSingle,
    finishedSingle,
    singleContractCampaign,

    // complete contract
    complete,
    completing,
    completingError,
    completingResult,
  };
};
